import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import { fetcher } from "@/lib/api";

/**
 * Public integration health as a plain-language traffic light.
 * Client cue: honest, readable "as if to a child".
 */

const STATUS_CONFIG = {
  ok: {
    color: "#10B981",
    bg: "#ECFDF5",
    ring: "#A7F3D0",
    Icon: CheckCircle2,
    labelEs: "Al día",
    labelGl: "Ao día",
  },
  warn: {
    color: "#F59E0B",
    bg: "#FFFBEB",
    ring: "#FDE68A",
    Icon: AlertCircle,
    labelEs: "Con retraso",
    labelGl: "Con atraso",
  },
  down: {
    color: "#EF4444",
    bg: "#FEF2F2",
    ring: "#FECACA",
    Icon: XCircle,
    labelEs: "Sin conexión",
    labelGl: "Sen conexión",
  },
};

// Map integration keys to plain-language copy
const INTEGRATION_COPY = {
  sigeus: {
    es: {
      title: "Recogida y pesajes",
      sub: "Datos de contenedores, camiones y pesajes en cada recogida.",
    },
    gl: {
      title: "Recollida e pesaxes",
      sub: "Datos de contedores, camións e pesaxes en cada recollida.",
    },
  },
  tristrash: {
    es: {
      title: "Flota y ubicación GPS",
      sub: "Posición en tiempo real de los vehículos de recogida.",
    },
    gl: {
      title: "Flota e localización GPS",
      sub: "Posición en tempo real dos vehículos de recollida.",
    },
  },
  anonymizer: {
    es: {
      title: "Anonimización de imágenes",
      sub: "Cada foto de incidencia se procesa con IA para difuminar caras y matrículas antes de mostrarla.",
    },
    gl: {
      title: "Anonimización de imaxes",
      sub: "Cada foto de incidencia procésase con IA para difuminar caras e matrículas antes de mostrala.",
    },
  },
  esg: {
    es: {
      title: "Indicadores ESG",
      sub: "8 indicadores territoriales agregados desde las fuentes de datos.",
    },
    gl: {
      title: "Indicadores ESG",
      sub: "8 indicadores territoriais agregados desde as fontes de datos.",
    },
  },
};

function statusFromRaw(item) {
  // Map any provider payload to ok|warn|down
  const s = String(item?.status || item?.state || "").toLowerCase();
  if (["ok", "healthy", "live", "active", "green"].includes(s)) return "ok";
  if (["degraded", "warn", "warning", "stale", "amber", "partial"].includes(s)) return "warn";
  if (["down", "error", "fail", "red", "off", "unhealthy"].includes(s)) return "down";
  return "warn";
}

// Map upstream `code` values → our copy keys. Accept both the new public
// codes (Fase 5 iter40 · post-provider-hiding) and the legacy internal codes
// for backwards compatibility with any cached client.
const CODE_ALIASES = {
  "container-system": "sigeus",
  "fleet-operations": "tristrash",
  tristrash: "tristrash",
  tristrash_ops: "tristrash",
  fleet: "tristrash",
  sigeus: "sigeus",
  sigeus_sync: "sigeus",
  distromel: "sigeus",
  anonymizer: "anonymizer",
  anon: "anonymizer",
  esg: "esg",
  esg_rollups: "esg",
};

function pickRelevantIntegrations(payload) {
  if (!payload) return [];
  const rows = Array.isArray(payload.integrations) ? payload.integrations : [];
  const seen = new Set();
  const items = [];
  for (const r of rows) {
    const code = String(r?.code || r?.key || "").toLowerCase();
    const mapped = CODE_ALIASES[code] || code;
    if (!INTEGRATION_COPY[mapped]) continue;
    if (seen.has(mapped)) continue;
    seen.add(mapped);
    items.push({
      key: mapped,
      status: statusFromRaw(r),
      updated_at: r?.last_check_at || r?.updated_at || null,
      brand_public: r?.brand_public || null,
      raw: r,
    });
  }
  // Include ESG derived from summary block if present
  if (!seen.has("esg") && payload?.summary) {
    const s = payload.summary;
    const blockedCount = Number(s.esg_kpis_blocked || 0);
    items.push({
      key: "esg",
      status: blockedCount === 0 ? "ok" : blockedCount <= 2 ? "warn" : "down",
      updated_at: payload.computed_at || null,
      raw: s,
    });
  }
  return items;
}

export default function ImpactoIntegrationsHealth() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data, error, isLoading } = useSWR(
    "/public/integration-status",
    fetcher,
    { revalidateOnFocus: false, refreshInterval: 5 * 60 * 1000 }
  );

  const items = pickRelevantIntegrations(data);

  // Empty state honest: si no llega el endpoint o está vacío, mostrar mensaje
  // en lenguaje llano, no un panel gris.
  if (error || (!isLoading && items.length === 0)) {
    return (
      <div
        className="rounded-3xl border border-salnes-stone/15 bg-salnes-sand/30 p-8 text-center"
        data-testid="impacto-health-empty"
      >
        <div className="text-salnes-stone text-sm">
          {lang === "es"
            ? "El estado de las integraciones se está publicando. Vuelve en unos minutos."
            : "O estado das integracións estase a publicar. Volve en uns minutos."}
        </div>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5"
      data-testid="impacto-health-grid"
    >
      {items.map((it, idx) => {
        const cfg = STATUS_CONFIG[it.status] || STATUS_CONFIG.warn;
        const copy = INTEGRATION_COPY[it.key]?.[lang] || {
          title: it.key,
          sub: "",
        };
        const Icon = cfg.Icon;
        return (
          <motion.div
            key={it.key}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.06 }}
            className="rounded-2xl bg-white border border-salnes-stone/10 p-6 flex items-start gap-4"
            data-testid={`impacto-health-${it.key}`}
            data-status={it.status}
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: cfg.bg, color: cfg.color, boxShadow: `0 0 0 4px ${cfg.ring}` }}
              aria-hidden
            >
              <Icon size={22} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-serif text-lg text-salnes-ink leading-tight">
                  {copy.title}
                </div>
                <span
                  className="text-[10px] uppercase tracking-[0.22em] font-medium rounded-full px-2 py-0.5"
                  style={{ background: cfg.bg, color: cfg.color }}
                >
                  {lang === "es" ? cfg.labelEs : cfg.labelGl}
                </span>
              </div>
              {it.brand_public && (
                <div
                  className="mt-1.5 text-[10px] uppercase tracking-[0.20em] text-salnes-stone"
                  data-testid={`impacto-health-brand-${it.key}`}
                >
                  {it.brand_public}
                </div>
              )}
              <p className="mt-1 text-salnes-stone text-sm leading-relaxed">
                {copy.sub}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
